import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Priority Badge Component
const PriorityBadge = ({ priority }) => {
  const config = {
    hot: { bg: 'bg-red-100', text: 'text-red-700', icon: '🔥', label: 'Hot' },
    warm: { bg: 'bg-orange-100', text: 'text-orange-700', icon: '🌡️', label: 'Warm' },
    medium: { bg: 'bg-blue-100', text: 'text-blue-700', icon: '📊', label: 'Medium' },
    cold: { bg: 'bg-gray-100', text: 'text-gray-700', icon: '❄️', label: 'Cold' }
  };
  const c = config[priority] || config.medium;
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      {c.icon} {c.label}
    </span>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = {
    pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending' },
    completed: { bg: 'bg-green-100', text: 'text-green-700', label: 'Completed' },
    skipped: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Skipped' },
    rescheduled: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Rescheduled' }
  };
  const c = config[status] || config.pending;
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
};

// Follow-up Card Component
const FollowUpCard = ({ followUp, onAction, language }) => {
  const isOverdue = followUp.status === 'pending' && new Date(followUp.scheduled_at) < new Date();
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('hi-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`bg-white rounded-xl p-4 shadow-sm border ${isOverdue ? 'border-red-300 bg-red-50' : 'border-gray-100'} hover:shadow-md transition`}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-800 truncate">{followUp.school_name || followUp.school?.school_name || 'Unknown School'}</h3>
          <p className="text-sm text-gray-500">{followUp.school?.district}, {followUp.school?.state}</p>
        </div>
        <div className="flex gap-2">
          <PriorityBadge priority={followUp.priority} />
          <StatusBadge status={followUp.status} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-sm mb-3">
        <div>
          <span className="text-gray-500">{language === 'hi' ? 'समय:' : 'Time:'}</span>
          <span className={`ml-1 font-medium ${isOverdue ? 'text-red-600' : 'text-gray-700'}`}>
            {formatDate(followUp.scheduled_at)}
          </span>
          {isOverdue && <span className="ml-1 text-red-500 text-xs">(Overdue!)</span>}
        </div>
        <div>
          <span className="text-gray-500">Score:</span>
          <span className="ml-1 font-medium text-blue-600">{followUp.lead_score || 0}/100</span>
        </div>
        {followUp.school?.whatsapp_number && (
          <div className="col-span-2">
            <span className="text-gray-500">📱</span>
            <span className="ml-1 font-medium text-green-600">{followUp.school.whatsapp_number}</span>
          </div>
        )}
      </div>

      {followUp.notes && (
        <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded mb-3 line-clamp-2">{followUp.notes}</p>
      )}

      <div className="flex gap-2 flex-wrap">
        {followUp.status === 'pending' && (
          <>
            <button
              onClick={() => onAction('complete', followUp.id)}
              className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600"
            >
              ✅ {language === 'hi' ? 'Complete' : 'Complete'}
            </button>
            <button
              onClick={() => onAction('reschedule', followUp.id)}
              className="px-3 py-1.5 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600"
            >
              📅 {language === 'hi' ? 'Reschedule' : 'Reschedule'}
            </button>
            <button
              onClick={() => onAction('skip', followUp.id)}
              className="px-3 py-1.5 bg-gray-400 text-white rounded-lg text-sm hover:bg-gray-500"
            >
              ⏭️ Skip
            </button>
          </>
        )}
        {followUp.school?.whatsapp_number && (
          <a
            href={`https://wa.me/91${followUp.school.whatsapp_number}?text=${encodeURIComponent(followUp.suggested_message || '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
          >
            💬 WhatsApp
          </a>
        )}
        <button
          onClick={() => onAction('view', followUp)}
          className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
        >
          👁️ {language === 'hi' ? 'देखें' : 'View'}
        </button>
      </div>
    </div>
  );
};

// AI Suggestions Modal
const AISuggestionsModal = ({ isOpen, onClose, school, suggestions, language }) => {
  if (!isOpen || !suggestions) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b bg-purple-50">
          <h3 className="text-lg font-semibold">🤖 AI {language === 'hi' ? 'सुझाव' : 'Suggestions'}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)] space-y-4">
          {/* School Info */}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="font-semibold">{suggestions.school?.name}</p>
            <p className="text-sm text-gray-600">{suggestions.school?.district}, {suggestions.school?.state}</p>
          </div>

          {/* Lead Score */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-700 mb-2">🎯 Lead Score</h4>
            <div className="flex items-center gap-4">
              <div className="text-4xl font-bold text-blue-600">{suggestions.ai_analysis?.lead_score?.score || 0}</div>
              <div className="flex-1">
                <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                    style={{ width: `${suggestions.ai_analysis?.lead_score?.score || 0}%` }}
                  />
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Priority: <PriorityBadge priority={suggestions.ai_analysis?.lead_score?.priority} />
                </p>
              </div>
            </div>
            <div className="mt-3 space-y-1">
              {suggestions.ai_analysis?.lead_score?.factors?.map((f, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-gray-600">{f.factor}</span>
                  <span className="text-green-600 font-medium">+{f.points}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Optimal Time */}
          <div className="bg-green-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-700 mb-2">⏰ {language === 'hi' ? 'Best Time' : 'Optimal Time'}</h4>
            <p className="text-lg font-bold text-green-600">{suggestions.ai_analysis?.optimal_time?.suggested_time_ist}</p>
            <ul className="mt-2 text-sm text-gray-600 space-y-1">
              {suggestions.ai_analysis?.optimal_time?.reasoning?.map((r, i) => (
                <li key={i}>• {r}</li>
              ))}
            </ul>
          </div>

          {/* Suggested Messages */}
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-700">💬 {language === 'hi' ? 'सुझाए गए Messages' : 'Suggested Messages'}</h4>
            
            <div className="bg-green-50 rounded-lg p-3">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-green-700">WhatsApp</span>
                <button
                  onClick={() => navigator.clipboard.writeText(suggestions.ai_analysis?.messages?.whatsapp?.message || '')}
                  className="text-xs text-green-600 hover:underline"
                >
                  📋 Copy
                </button>
              </div>
              <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">
                {suggestions.ai_analysis?.messages?.whatsapp?.message}
              </pre>
            </div>

            <div className="bg-blue-50 rounded-lg p-3">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-blue-700">Email</span>
                <button
                  onClick={() => navigator.clipboard.writeText(suggestions.ai_analysis?.messages?.email?.message || '')}
                  className="text-xs text-blue-600 hover:underline"
                >
                  📋 Copy
                </button>
              </div>
              <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">
                {suggestions.ai_analysis?.messages?.email?.message}
              </pre>
            </div>
          </div>

          {/* Recommendation */}
          <div className="bg-yellow-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-700 mb-2">💡 {language === 'hi' ? 'सिफारिश' : 'Recommendation'}</h4>
            <p className="text-gray-700">{suggestions.recommendation?.action}</p>
            <p className="text-sm text-gray-600 mt-1">
              Channel: <span className="font-medium">{suggestions.recommendation?.channel}</span>
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
            {language === 'hi' ? 'बंद करें' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Follow-ups Page
export default function FollowUpsPage({ token }) {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [followUps, setFollowUps] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [activeFilter, setActiveFilter] = useState('pending');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [autoScheduling, setAutoScheduling] = useState(false);
  const [creatingDemo, setCreatingDemo] = useState(false);
  const [selectedFollowUp, setSelectedFollowUp] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [showAiModal, setShowAiModal] = useState(false);

  useEffect(() => {
    loadData();
  }, [activeFilter, priorityFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [followUpsRes, dashboardRes] = await Promise.all([
        fetch(`${API_URL}/api/school-finder/follow-ups?status=${activeFilter}${priorityFilter ? `&priority=${priorityFilter}` : ''}&limit=100`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/school-finder/follow-ups/dashboard`, { headers }).then(r => r.json())
      ]);
      
      setFollowUps(followUpsRes.follow_ups || []);
      setDashboard(dashboardRes);
    } catch (err) {
      console.error('Error loading follow-ups:', err);
    }
    setLoading(false);
  };

  const handleAction = async (action, data) => {
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    
    try {
      if (action === 'complete') {
        await fetch(`${API_URL}/api/school-finder/follow-ups/${data}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({ status: 'completed' })
        });
        loadData();
      } else if (action === 'skip') {
        await fetch(`${API_URL}/api/school-finder/follow-ups/${data}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({ status: 'skipped' })
        });
        loadData();
      } else if (action === 'reschedule') {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(10, 0, 0, 0);
        
        await fetch(`${API_URL}/api/school-finder/follow-ups/${data}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({ rescheduled_to: tomorrow.toISOString() })
        });
        loadData();
      } else if (action === 'view') {
        setSelectedFollowUp(data);
        // Load AI suggestions
        const res = await fetch(`${API_URL}/api/school-finder/follow-ups/ai-suggest/${data.school_id}`, { headers });
        const suggestions = await res.json();
        setAiSuggestions(suggestions);
        setShowAiModal(true);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleAutoSchedule = async () => {
    if (!window.confirm(language === 'hi' ? 'Hot leads के लिए auto follow-ups schedule करें?' : 'Auto-schedule follow-ups for hot leads?')) return;
    
    setAutoScheduling(true);
    try {
      const res = await fetch(`${API_URL}/api/school-finder/follow-ups/auto-schedule?priority=hot&limit=50`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      alert(data.message_hi || `${data.scheduled_count} follow-ups scheduled`);
      loadData();
    } catch (err) {
      console.error('Error:', err);
    }
    setAutoScheduling(false);
  };

  const handleCreateDemoData = async () => {
    setCreatingDemo(true);
    try {
      const res = await fetch(`${API_URL}/api/school-finder/follow-ups/create-demo-data`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        alert(data.message_hi || `${data.created_count} demo follow-ups created`);
        loadData();
      } else {
        alert('Error creating demo data');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Error creating demo data');
    }
    setCreatingDemo(false);
  };

  const handleClearDemoData = async () => {
    if (!window.confirm(language === 'hi' ? 'सभी demo data delete करें?' : 'Clear all demo data?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/school-finder/follow-ups/clear-demo-data`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      alert(data.message_hi || `${data.deleted_count} demo follow-ups deleted`);
      loadData();
    } catch (err) {
      console.error('Error:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{language === 'hi' ? 'Loading...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6" data-testid="follow-ups-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            ⏰ {language === 'hi' ? 'Follow-up Reminders' : 'Follow-up Reminders'}
          </h1>
          <p className="text-gray-500 text-sm">
            {language === 'hi' ? 'AI-powered smart follow-ups' : 'AI-powered smart follow-ups'}
          </p>
        </div>
        
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={handleCreateDemoData}
            disabled={creatingDemo}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 flex items-center gap-2"
            data-testid="create-demo-btn"
          >
            {creatingDemo ? '⏳' : '🧪'} {language === 'hi' ? 'Demo Data' : 'Demo Data'}
          </button>
          <button
            onClick={handleClearDemoData}
            className="px-4 py-2 bg-red-400 text-white rounded-lg hover:bg-red-500 flex items-center gap-2"
            data-testid="clear-demo-btn"
          >
            🗑️ {language === 'hi' ? 'Clear Demo' : 'Clear Demo'}
          </button>
          <button
            onClick={handleAutoSchedule}
            disabled={autoScheduling}
            className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 flex items-center gap-2"
            data-testid="auto-schedule-btn"
          >
            {autoScheduling ? '⏳' : '🤖'} {language === 'hi' ? 'Auto Schedule' : 'Auto Schedule'}
          </button>
          <button 
            onClick={loadData}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Dashboard Stats */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-yellow-100">
            <p className="text-sm text-gray-500">{language === 'hi' ? 'Pending' : 'Pending'}</p>
            <p className="text-2xl font-bold text-yellow-600">{dashboard.summary?.total_pending || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-red-100">
            <p className="text-sm text-gray-500">Overdue</p>
            <p className="text-2xl font-bold text-red-600">{dashboard.summary?.overdue || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-orange-100">
            <p className="text-sm text-gray-500">{language === 'hi' ? 'आज Due' : 'Due Today'}</p>
            <p className="text-2xl font-bold text-orange-600">{dashboard.summary?.today_due || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-green-100">
            <p className="text-sm text-gray-500">{language === 'hi' ? 'इस Week' : 'This Week'}</p>
            <p className="text-2xl font-bold text-green-600">{dashboard.summary?.completed_this_week || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-blue-100">
            <p className="text-sm text-gray-500">Response Rate</p>
            <p className="text-2xl font-bold text-blue-600">{dashboard.summary?.response_rate || 0}%</p>
          </div>
        </div>
      )}

      {/* Alerts */}
      {dashboard?.alerts?.filter(a => a).length > 0 && (
        <div className="space-y-2 mb-6">
          {dashboard.alerts.filter(a => a).map((alert, idx) => (
            <div key={idx} className={`p-3 rounded-lg flex items-center gap-3 ${
              alert.type === 'danger' ? 'bg-red-50 text-red-700' :
              alert.type === 'warning' ? 'bg-yellow-50 text-yellow-700' :
              'bg-blue-50 text-blue-700'
            }`}>
              <span className="text-xl">{alert.type === 'danger' ? '🚨' : alert.type === 'warning' ? '⚠️' : 'ℹ️'}</span>
              <span>{alert.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        <div className="flex bg-white rounded-lg p-1 shadow-sm">
          {['pending', 'completed', 'all'].map(status => (
            <button
              key={status}
              onClick={() => setActiveFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                activeFilter === status ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {status === 'pending' ? '⏳ Pending' : status === 'completed' ? '✅ Completed' : '📋 All'}
            </button>
          ))}
        </div>
        
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm bg-white"
        >
          <option value="">{language === 'hi' ? 'सभी Priority' : 'All Priorities'}</option>
          <option value="hot">🔥 Hot</option>
          <option value="warm">🌡️ Warm</option>
          <option value="cold">❄️ Cold</option>
        </select>
      </div>

      {/* Priority Summary */}
      {dashboard?.by_priority && (
        <div className="flex gap-4 mb-6">
          <div className="flex items-center gap-2 bg-red-50 px-3 py-1 rounded-full">
            <span>🔥</span>
            <span className="font-medium text-red-700">{dashboard.by_priority.hot || 0} Hot</span>
          </div>
          <div className="flex items-center gap-2 bg-orange-50 px-3 py-1 rounded-full">
            <span>🌡️</span>
            <span className="font-medium text-orange-700">{dashboard.by_priority.warm || 0} Warm</span>
          </div>
          <div className="flex items-center gap-2 bg-blue-50 px-3 py-1 rounded-full">
            <span>❄️</span>
            <span className="font-medium text-blue-700">{dashboard.by_priority.cold || 0} Cold</span>
          </div>
        </div>
      )}

      {/* Follow-ups Grid */}
      {followUps.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center">
          <span className="text-6xl mb-4 block">📭</span>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            {language === 'hi' ? 'कोई Follow-up नहीं' : 'No Follow-ups'}
          </h3>
          <p className="text-gray-500 mb-4">
            {language === 'hi' ? 'Auto Schedule button से नए follow-ups बनाएं' : 'Create new follow-ups using Auto Schedule'}
          </p>
          <button
            onClick={handleAutoSchedule}
            className="px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
          >
            🤖 {language === 'hi' ? 'Auto Schedule करें' : 'Auto Schedule Now'}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {followUps.map(followUp => (
            <FollowUpCard
              key={followUp.id}
              followUp={followUp}
              onAction={handleAction}
              language={language}
            />
          ))}
        </div>
      )}

      {/* AI Suggestions Modal */}
      <AISuggestionsModal
        isOpen={showAiModal}
        onClose={() => setShowAiModal(false)}
        school={selectedFollowUp?.school}
        suggestions={aiSuggestions}
        language={language}
      />
    </div>
  );
}
